import React, { useEffect, useState } from "react";

import {
  Container,
  Table,
  DownloadButton,
  Header,
  Content,
  UploadSide,
  ModelSide,
  TableColumns,
  TableList,
  TableItem,
  ActionsContent,
  CancelButton,
  ConfirmButton,
  ModelSideColumn,
  HistoryContent,
  UploadContent,
  TableHeader,
  TableFooter,
} from "./styles";

//Firebase and context
import { Timestamp } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";

//Custom components
import Upload from "@components/Upload/Upload";
import ProfileButton from "@components/ProfileButton/ProfileButton";

//Icons
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { FiDownload } from "react-icons/fi";
import cpfIcon from "@assets/cpfIcon.svg";

//Utils
import Modal from "react-modal";
import DotLoader from "react-spinners/ClipLoader";
import { datetimeHelper } from "@helpers/datetime";
import ModalConfirm from "@components/Modal/ModalConfirm/ModalConfirm";
import { AiOutlineEye } from "react-icons/ai";
import ModalDetailsAdmin from "@components/Modal/ModalDetailsAdmin/ModalDetailsAdmin";
import { requiredImportHeader } from "@constants/requiredImportHeader";
import HistoryList from "./components/HistoryList/HistoryList";
import { ImportClientValidation } from "@domain/models/ImportClientValidation";
import { Client } from "@domain/models/Client";
import {
  ImportClientsUseCase,
  ImportDbClientsUseCase,
  RemoveOldClientsUseCase,
  ValidateClientImport,
} from "@useCases/index";
import { HttpImportRepository, HttpRemoveRepository } from "@data/repositories/index";
import { api } from "@data/api";
import useImportHistory from "@hooks/useImportHistory";
import { DbClient } from "@domain/models/DbClient";
import { ImportHistory } from "@domain/models/ImportHistory";
import { ProgressBar } from "@components/ProgressBar/ProgressBar";
import useImportJob from "@hooks/useImportJob";
import { JobProgressBar } from "@components/JobProgressBar/JobProgressBar";
import useRemoveJob from "@hooks/useRemoveJob";

Modal.setAppElement("#root");

const UploadPage = () => {
  const { getImportHistory } = useImportHistory();
  const { importJob, watchPendingImportJob, cleanImportJob } = useImportJob();
  const { removeJob, watchPendingRemoveJob, cleanRemoveJob } = useRemoveJob();

  const [importHistory, setImportHistory] = useState<ImportHistory[]>([]);

  const [arrayTable, setArrayTable] = useState<ImportClientValidation[]>([]);
  const [showTable, setShowTable] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [importData, setImportData] = useState<Client[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalFullData, setModalFullData] = useState(false);
  const [currentClientId, setCurrentClientId] = useState("");

  const [removelOldClientsLoading, setRemoveOldClientsLoading] = useState(false);
  const [verifyExistsLoading, setVerifyExistsLoading] = useState(false);

  useEffect(() => {
    fetchImportHistory();
  }, []);

  useEffect(() => {
    if (importJob?.status === "completed" || removeJob?.status === "completed") {
      fetchImportHistory();
    }
  }, [importJob, removeJob]);

  const fetchImportHistory = async () => {
    return getImportHistory().then((data) => {
      setImportHistory(data);
    });
  };

  const openModal = () => {
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const clearOperation = () => {
    setCancel(false);
    setArrayTable([]);
    setShowTable(false);
    setSelectAll(false);
    setImportData([]);
    setLoading(false);
  };

  const verifyExists = async (data: Client[]) => {
    setVerifyExistsLoading(true);
    const useCase = new ValidateClientImport(new HttpImportRepository(api));

    useCase
      .execute(data)
      .then((validatedClients) => {
        if (validatedClients.length > 0) {
          setArrayTable(validatedClients);
          setShowTable(true);
        }

        openModal();
      })
      .catch(() => {
        clearOperation();
      })
      .finally(() => setVerifyExistsLoading(false));
  };

  const uploadClients = async () => {
    setLoading(true);

    const notSelectedClients = [];
    const selectedClients = [];

    for (const item of arrayTable) {
      if (item.status === false) {
        const client = importData.find((x) => x.id === item.id);
        notSelectedClients.push(client);
      }
    }

    for (const client of importData) {
      const result = notSelectedClients.find((x) => x.id === client.id);

      if (!result) {
        selectedClients.push(client);
      }
    }

    const useCase = new ImportClientsUseCase(new HttpImportRepository(api));

    useCase
      .execute({ selectedClients, notSelectedClients })
      .then((job) => {
        watchPendingImportJob(job.id);
      })
      .finally(() => {
        clearOperation();
        setLoading(false);
      });
  };

  const uploadDbClients = async (type: "auxBrasil" | "indeferidosDb" | "deferidos") => {
    setLoading(true);

    const useCase = new ImportDbClientsUseCase(new HttpImportRepository(api));

    const dbClients: DbClient[] = importData.map((item) => ({
      id: item.id,
      name: item.name,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    }));

    useCase
      .execute({ type, dbClients })
      .then((job) => {
        watchPendingImportJob(job.id);
      })
      .finally(() => {
        clearOperation();
        setLoading(false);
      });
  };

  const removeOld = async () => {
    setRemoveOldClientsLoading(true);

    const useCase = new RemoveOldClientsUseCase(new HttpRemoveRepository(api));

    useCase
      .execute()
      .then((job) => {
        watchPendingRemoveJob(job.id);
      })
      .finally(() => {
        closeModal();
        setRemoveOldClientsLoading(false);
      });
  };

  const setData = async (selectedOption, data) => {
    if (cancel === false && data.length != 0) {
      switch (selectedOption) {
        case "indeferidos":
          await verifyExists(data);
          setSelectedOption(selectedOption);
          setImportData(data);
          break;
        case "indeferidosDb":
          setSelectedOption(selectedOption);
          setImportData(data);
          break;
        case "auxBrasil":
          setSelectedOption(selectedOption);
          setImportData(data);
          break;
        case "deferidos":
          setSelectedOption(selectedOption);
          setImportData(data);
          break;
      }
    } else {
      alert("Impossível importar! planilha vazia.");
    }
  };

  const handleException = (item) => {
    const result = arrayTable.findIndex((x) => x.cpf === item.cpf);

    const array1 = arrayTable.slice(0, result);
    const array2 = arrayTable.slice(result + 1, arrayTable.length);

    array1.push({
      ...item,
      status: !item.status,
    });

    setArrayTable(array1.concat(array2));
  };

  const changeSelectAll = () => {
    const newArray = [];

    for (const i in arrayTable) {
      newArray.push({
        ...arrayTable[i],
        status: !selectAll,
      });
    }
    setArrayTable(newArray);
    setSelectAll(!selectAll);
  };

  const downloadModelos = async (name) => {
    const storage = getStorage();
    const pathReference = ref(
      storage,
      `gs://${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${name}.csv`
    );

    getDownloadURL(pathReference)
      .then((url) => {
        // `url` is the download URL for 'images/stars.jpg'
        window.open(url, "_blank", "noopener,noreferrer");
      })
      .catch(() => {
        alert("Ocorreu um erro");
      });
  };

  const handleOpenClientModal = (clientId: string) => {
    setCurrentClientId(clientId);
    setModalFullData(true);
  };

  return (
    <Container>
      <JobProgressBar
        job={importJob ? importJob : removeJob}
        type={importJob ? "import" : "remove"}
        onClose={() => {
          cleanImportJob();
          cleanRemoveJob();
        }}
      />
      <Header>
        <h1>Adicionar clientes</h1>
        <ProfileButton arrowColor="var(grey2)" />
      </Header>

      <Content>
        <UploadContent>
          <UploadSide>
            <h3>Importação</h3>

            <Upload
              importData={importData}
              optionChanged={() => clearOperation()}
              setData={(selectedOption, data) => {
                setData(selectedOption, data);
              }}
            />

            {importData.length > 0 && <ProgressBar success={!verifyExistsLoading} />}

            {importData.length > 0 && !showTable && !verifyExistsLoading && (
              <ActionsContent>
                <CancelButton onClick={() => clearOperation()}>
                  <p>Cancelar importação</p>
                </CancelButton>
                {loading ? (
                  <DotLoader size={20} color="var(--red)" loading={loading} speedMultiplier={1.5} />
                ) : (
                  <ConfirmButton
                    disabled={false}
                    onClick={(e) => {
                      e.preventDefault();
                      if (selectedOption === "indeferidos") {
                        return uploadClients();
                      }
                      if (selectedOption === "indeferidosDb") {
                        return uploadDbClients("indeferidosDb");
                      }
                      if (selectedOption === "auxBrasil") {
                        return uploadDbClients("auxBrasil");
                      }
                      if (selectedOption === "deferidos") {
                        return uploadDbClients("deferidos");
                      }
                    }}
                  >
                    <p>Inserir no mapa</p>
                  </ConfirmButton>
                )}
              </ActionsContent>
            )}
          </UploadSide>
          <ModelSide>
            <ModelSideColumn>
              <h3>Baixar modelos</h3>

              <DownloadButton onClick={async () => await downloadModelos("auxBrasil")}>
                <FiDownload />
                Modelo Aux Brasil
              </DownloadButton>

              <DownloadButton onClick={async () => await downloadModelos("deferidos")}>
                <FiDownload />
                Modelo Deferidos
              </DownloadButton>

              <DownloadButton onClick={async () => await downloadModelos("indeferidos")}>
                <FiDownload />
                Modelo Indeferidos completo
              </DownloadButton>

              <DownloadButton
                onClick={async () => await downloadModelos("indeferidos-obrigatorias")}
              >
                <FiDownload />
                Modelo Indeferidos obrigatórias
              </DownloadButton>
            </ModelSideColumn>
          </ModelSide>
          <ModelSideColumn>
            <h3>Colunas obrigatórias indeferidos</h3>

            <div>
              {requiredImportHeader.map((item, index) => (
                <h4 key={index}>{item}</h4>
              ))}
            </div>
          </ModelSideColumn>
        </UploadContent>
        {arrayTable.length > 0 && (
          <Table>
            <TableHeader>
              <h1>Os itens a seguir já estão no banco de dados. Deseja inserir no mapa?</h1>
            </TableHeader>

            <TableColumns>
              {selectAll ? (
                <MdCheckBox
                  size={20}
                  style={{ borderRadius: 20 }}
                  onClick={() => changeSelectAll()}
                />
              ) : (
                <MdCheckBoxOutlineBlank size={20} onClick={() => changeSelectAll()} />
              )}
              <h3>Nome</h3>
              <h3>CPF</h3>
              <h3>Indeferidos Mapa</h3>
              <h3>Indeferidos BD</h3>
              <h3>Aux Brasil</h3>
              <h3>Deferidos</h3>
              <h3>Data de inserção no mapa</h3>
              <h3>Ações</h3>
            </TableColumns>
            <TableList>
              {arrayTable.map((item, index) => (
                <TableItem key={index}>
                  {item.status ? (
                    <MdCheckBox size={20} cursor="pointer" onClick={() => handleException(item)} />
                  ) : (
                    <MdCheckBoxOutlineBlank
                      size={20}
                      cursor="pointer"
                      onClick={() => handleException(item)}
                    />
                  )}
                  <h1>{item.name}</h1>
                  <div>
                    <img src={cpfIcon} alt="img img" />
                    <h3>{item.cpf}</h3>
                  </div>

                  <h3>{item.indeferidosMapa}</h3>
                  <h3>{item.indeferidos}</h3>
                  <h3>{item.auxBrasil}</h3>
                  <h3>{item.deferidos}</h3>
                  <h3>{datetimeHelper.getDate(item.date)}</h3>
                  <div>
                    <AiOutlineEye
                      onClick={() => handleOpenClientModal(item.id)}
                      cursor="pointer"
                      size={18}
                      color="#808B9F"
                      fill="#808B9F"
                    />
                  </div>
                </TableItem>
              ))}
            </TableList>
            <TableFooter>
              <h3>{arrayTable ? arrayTable.length : 0} clientes</h3>
            </TableFooter>

            <ActionsContent>
              {loading ? (
                <DotLoader size={20} color="var(--red)" loading={loading} speedMultiplier={1.5} />
              ) : (
                <>
                  <CancelButton onClick={() => clearOperation()}>
                    <p>Cancelar importação</p>
                  </CancelButton>
                  <ConfirmButton
                    disabled={false}
                    onClick={async () => {
                      await uploadClients();
                    }}
                  >
                    <p>Inserir no mapa</p>
                  </ConfirmButton>
                </>
              )}
            </ActionsContent>
          </Table>
        )}
      </Content>

      <HistoryContent>
        <HistoryList
          importHistory={importHistory}
          onUndoImport={(job) => {
            watchPendingImportJob(job.id);
          }}
        />
      </HistoryContent>

      <Modal
        isOpen={modalVisible}
        onRequestClose={closeModal}
        overlayClassName="modal-overlay"
        className="modal-content-auto-width"
        contentLabel="Example Modal"
      >
        <ModalConfirm
          text="Deseja excluir os clientes adicionados há 3 meses atrás?"
          option="Sim"
          rejectOption="Não"
          loading={removelOldClientsLoading}
          onCancel={() => {
            closeModal();
            clearOperation();
          }}
          onReject={closeModal}
          onConfirm={() => {
            removeOld();
          }}
        />
      </Modal>

      <Modal
        isOpen={modalFullData}
        onRequestClose={() => setModalFullData(false)}
        overlayClassName="modal-overlay"
        className="modal-content-auto-width"
        contentLabel="Example Modal"
      >
        {currentClientId ? (
          <ModalDetailsAdmin
            clientId={currentClientId}
            onClose={() => setModalFullData(false)}
            //   onStreetView={() => {
            //     setModalFullData(false);
            //   }}
          />
        ) : (
          <></>
        )}
      </Modal>
    </Container>
  );
};

export default UploadPage;
